import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, CreditCard, FileDown, History, Loader2, Mail, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile } from "@/lib/pdf";
import { useT } from "@/i18n";
import { fmtPrice } from "@/lib/content";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useFormGuard } from "@/lib/formGuard";
import { FieldError, errCls, focusFirstError, isAddress, isDigits, isEmail, isFullName, isNip, isPesel, isPhone, isRegon } from "@/lib/validate";
import { ModeSelector, entityModeOf } from "@/components/landing/order/ModeSelector";
import { ProxiesFields } from "@/components/landing/order/ProxiesFields";
import { OrderFields, Field } from "@/components/landing/order/OrderFields";
import { OwuStatement } from "@/components/landing/OwuStatement";
import { OrderStepper, STEP_KEYS } from "@/components/landing/order/OrderStepper";
import { OrderSummary } from "@/components/landing/order/OrderSummary";

const BILLING_FIELDS = {
  monthly: { price: "price_monthly", total: null },
  3: { price: "price_3", total: "total_3" },
  6: { price: "price_6", total: "total_6" },
  12: { price: "price_annual", total: "total_12" },
  24: { price: "price_24", total: "total_24" },
};

const BILLING_MONTHS = { monthly: 1, 3: 3, 6: 6, 12: 12, 24: 24 };

const TODAY_ISO = new Date().toISOString().slice(0, 10);
const EMPTY = {
  company: "", legal_form: "", nip: "", regon: "", krs: "", representative: "", email: "", phone: "",
  mailing_address: "", mailing_country: "PL", registered_address: "", start_date: TODAY_ISO, notes: "", pesel: "",
};
const OFFICE_ADDRESS = (a) => { const l = a.replace(/\s+/g, "").toLowerCase(); return l.includes("domaniewsk") && (l.includes("17/19") || l.includes("17-19")); };
const NO_CHOICE = { mode: null, kind: null, way: null };
const DRAFT_KEY = "tb-order-draft";
const DRAFT_TTL_MS = 24 * 3600 * 1000;

const loadDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    if (!d || Date.now() - d.at > DRAFT_TTL_MS) { localStorage.removeItem(DRAFT_KEY); return null; }
    return d;
  } catch { return null; }
};
const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

const inputCls = "bg-background";

export default function OrderDialog({ open, onOpenChange, packages, initialPackage, initialBilling, show24 = false, source = "" }) {
  const { t, lang } = useT();
  const [pkgId, setPkgId] = useState(initialPackage);
  const [billing, setBilling] = useState(initialBilling);
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [expiry, setExpiry] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [extraFields, setExtraFields] = useState([]);
  const { guard, refresh: refreshGuard, Honeypot, GuardNotice } = useFormGuard(open);
  const [extra, setExtra] = useState({});
  const [choice, setChoice] = useState(NO_CHOICE);
  const [proxies, setProxies] = useState([]);
  const [signatories, setSignatories] = useState([{ name: "", role: "" }]);
  const [extraEmails, setExtraEmails] = useState([]);
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingOrderId, setPendingOrderId] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [step, setStep] = useState(0);
  const [restored, setRestored] = useState(false);
  const [regUnavailable, setRegUnavailable] = useState(false);
  const contentRef = useRef(null);
  const mode = entityModeOf(choice);
  const W = t.order.wizard;
  const isKrs = mode === "existing" && form.legal_form && form.legal_form !== "jdg";
  const sigMax = form.legal_form === "sc" ? 4 : 2;
  useEffect(() => {
    setSignatories((prev) => (prev.length > sigMax ? prev.slice(0, sigMax) : prev));
  }, [sigMax]);
  const signatoriesStr = signatories.filter((s) => s.name.trim()).map((s) => `${s.name.trim()}${s.role ? ` – ${s.role}` : ""}`).join(", ");

  useEffect(() => {
    if (!open) return;
    api.get("/order-fields").then((r) => setExtraFields(r.data.fields || [])).catch(() => setExtraFields([]));
  }, [open]);

  useEffect(() => {
    if (!done || !expiry) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done, expiry]);

  const remaining = expiry ? Math.max(0, Math.floor((expiry - nowTs) / 1000)) : 0;
  const expired = expiry > 0 && remaining <= 0;
  const mmss = `${String(Math.floor(remaining / 60)).padStart(1, "0")}:${String(remaining % 60).padStart(2, "0")}`;

  const payNow = () => {    if (!orderRef?.pay_url) return;
    setPaying(true);
    window.location.assign(orderRef.pay_url);
  };

  const downloadContract = async () => {
    setDownloading(true);
    try {
      await downloadFile({ method: "post", url: "/contracts/pdf", data: submitted }, "Umowa.pdf");
    } catch (err) {
      toast.error(err.response?.status === 429 ? t.errors.too_many : t.order.pdf_err);
    } finally {
      setDownloading(false);
    }
  };

  const downloadProforma = async () => {
    if (!orderRef) return;
    setDownloading(true);
    try {
      await downloadFile({ method: "get", url: `/orders/${orderRef.id}/proforma.pdf`, params: { t: orderRef.t } }, "Proforma.pdf");
    } catch (err) {
      toast.error(err.response?.status === 429 ? t.errors.too_many : t.order.pdf_err);
    } finally {
      setDownloading(false);
    }
  };

  const resetAll = () => {
    setForm(EMPTY); setExtra({}); setProxies([]); setSignatories([{ name: "", role: "" }]); setExtraEmails([]); setConsent(false); setErrors({}); setChoice(NO_CHOICE); setStep(0); setRestored(false); setRegUnavailable(false);
  };

  useEffect(() => {
    if (!open) return;
    api.post("/track", { event: "wizard_open", source, path: window.location.pathname }).catch(() => {});
    setPkgId(initialPackage);
    setBilling(initialBilling);
    setDone(false);
    const d = loadDraft();
    if (d) {
      setForm({ ...EMPTY, ...d.form }); setExtra(d.extra || {}); setProxies(d.proxies || []); setSignatories(d.signatories?.length ? d.signatories : [{ name: "", role: "" }]); setExtraEmails(d.extraEmails || []); setChoice(d.choice || NO_CHOICE);
      setStep(Math.min(d.step || 0, 2)); setConsent(false); setErrors({}); setRestored(true);
    } else {
      resetAll();
    }
  }, [open, initialPackage, initialBilling]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || done) return;
    const dirty = choice.mode || Object.keys(EMPTY).some((k) => form[k] !== EMPTY[k]) || proxies.length || Object.keys(extra).length;
    if (!dirty) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ at: Date.now(), form, extra, proxies, signatories, extraEmails, choice, step }));
  }, [open, done, form, extra, proxies, choice, step]);

  const startOver = () => { clearDraft(); resetAll(); };

  useEffect(() => { contentRef.current?.scrollTo({ top: 0 }); }, [step, done]);

  const pkg = packages.find((p) => p.id === pkgId) || packages[0];
  const trPkg = (t.pricing.pkgs && t.pricing.pkgs[pkg.id]) || {};
  const pkgName = lang === "pl" ? pkg.name : trPkg.name || pkg.name;
  const billingLabels = { monthly: t.pricing.monthly, 3: t.pricing.m3, 6: t.pricing.m6, 12: t.pricing.annual, 24: t.pricing.m24 };
  const availableBillings = Object.keys(BILLING_FIELDS).filter((b) => pkg[BILLING_FIELDS[b].price] && (b !== "24" || show24));
  const effBilling = availableBillings.includes(billing) ? billing : availableBillings[0] || "monthly";
  const price = pkg[BILLING_FIELDS[effBilling].price] || "";
  const total = BILLING_FIELDS[effBilling].total ? pkg[BILLING_FIELDS[effBilling].total] : "";

  const [errors, setErrors] = useState({});
  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };
  const clearErr = (k) => errors[k] && setErrors({ ...errors, [k]: undefined });

  const validate = (withConsent = true) => {
    const er = {};
    const req = (k) => { if (!(form[k] || "").trim()) er[k] = t.errors.required; };
    req("company");
    if (mode !== "new_jdg") req("legal_form");
    if (mode === "existing") {
      if (!isNip(form.nip)) er.nip = form.nip.trim() ? t.errors.nip : W.errors_nip_required;
      if ((form.regon || "").trim() && !isRegon(form.regon)) er.regon = t.errors.regon;
      if (form.krs.trim() && !isDigits(form.krs, 10)) er.krs = t.errors.krs;
    } else if (form.nip.trim() && !isNip(form.nip)) er.nip = t.errors.nip;
    if (mode === "new_jdg" && !isPesel(form.pesel)) er.pesel = W.errors_pesel;
    if (isKrs) {
      if (!(signatories[0]?.name || "").trim()) er.sig_name_0 = t.errors.required;
      if (!(signatories[0]?.role || "").trim()) er.sig_role_0 = t.errors.required;
      signatories.forEach((s, i) => {
        if ((s.name || "").trim() && !(s.role || "").trim()) er[`sig_role_${i}`] = t.errors.required;
        if (!(s.name || "").trim() && (s.role || "").trim()) er[`sig_name_${i}`] = t.errors.required;
        if ((s.name || "").trim() && !isFullName(s.name)) er[`sig_name_${i}`] = t.errors.name;
      });
    } else if (mode !== "new_jdg") {
      req("representative");
      if ((form.representative || "").trim() && !isFullName(form.representative)) er.representative = t.errors.name;
    }
    if (!form.email.trim()) er.email = t.errors.required;
    else if (!isEmail(form.email)) er.email = t.errors.email;
    if (!form.phone.trim()) er.phone = t.errors.required;
    else if (!isPhone(form.phone)) er.phone = t.errors.phone;
    extraEmails.forEach((em, i) => { if (em.trim() && !isEmail(em.trim())) er[`xemail_${i}`] = t.errors.email; });
    if (mode !== "new_krs_s24") {
      req("mailing_address");
      if (form.mailing_address.trim() && OFFICE_ADDRESS(form.mailing_address)) er.mailing_address = W.errors_office_address;
      else if (form.mailing_address.trim() && !isAddress(form.mailing_address)) er.mailing_address = t.errors.address;
      extraFields.forEach((f) => { if (f.required && !(extra[f.key] || "").trim()) er[`x_${f.key}`] = t.errors.required; });
    }
    if (withConsent && !consent) er.consent = t.errors.consent;
    return er;
  };

  const fillFromRegistry = (d) => {
    const legal = d.legal_form && t.order.legal_forms[d.legal_form] ? d.legal_form : form.legal_form;
    setForm({ ...form, company: d.name || form.company, krs: d.krs || form.krs, legal_form: legal, nip: d.nip || form.nip, regon: d.regon || form.regon,
      registered_address: d.address || form.registered_address });
    setErrors({ ...errors, company: undefined, krs: undefined, legal_form: undefined, nip: undefined, registered_address: undefined });
  };

  const onLookupStatus = (s) => {
    if (s === "unavailable") setRegUnavailable(true);
    else if (s === "found") setRegUnavailable(false);
  };

  const setExtraVal = (key, v) => {
    setExtra({ ...extra, [key]: v });
    clearErr(`x_${key}`);
  };

  const buildPayload = () => ({
    ...form, package_id: pkg.id, package_name: pkg.name, billing: effBilling, price, total, lang, extra, entity_mode: mode, source,
    legal_form: mode === "new_jdg" ? "jdg" : form.legal_form, representative: mode === "new_jdg" ? form.company : (isKrs ? signatoriesStr : form.representative),
    signatories: isKrs ? signatories.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), role: s.role || "", represents: !!s.represents })) : [],
    extra_emails: extraEmails.map((e) => e.trim()).filter(Boolean),
    proxies: proxies.filter((p) => p.name.trim()), owu_consent: consent,
  });

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error(t.errors.summary);
      setStep(2);
      setTimeout(() => focusFirstError(er), 50);
      return;
    }
    setSending(true);
    try {
      const payload = buildPayload();
      const res = await api.post("/orders", { ...payload, ...(await guard()) });
      setPendingEmail(res.data.email || payload.email);
      setPendingOrderId(res.data.id || "");
      setResent(false);
      setDone(true);
      clearDraft();
      setRestored(false);
      setForm(EMPTY);
      setExtra({});
      setProxies([]);
      setSignatories([{ name: "", role: "" }]);
      setExtraEmails([]);
      setConsent(false);
      setErrors({});
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(status === 429 ? t.errors.too_many : typeof detail === "string" ? detail : t.order.toast_err);
      if (status === 400) refreshGuard();
    } finally {
      setSending(false);
    }
  };
  const goTo = (s) => { setStep(s); setErrors({}); };

  const resendVerification = async () => {
    if (!pendingOrderId) return;
    setResending(true);
    try {
      await api.post(`/orders/${pendingOrderId}/resend-verification`);
      setResent(true);
      toast.success(t.verify.resent);
    } catch (err) {
      toast.error(err.response?.status === 429 ? t.errors.too_many : t.verify.resend_err);
    } finally {
      setResending(false);
    }
  };

  const next = () => {
    if (step === 1 && !mode) {
      toast.error(W.step_mode_hint);
      return;
    }
    if (step === 2) {
      const er = validate(true);
      setErrors(er);
      if (Object.keys(er).length) {
        toast.error(t.errors.summary);
        focusFirstError(er);
        return;
      }
    }
    goTo(step + 1);
  };

  const stepperIndex = done ? (orderRef?.mode === "new_krs_deed" ? STEP_KEYS.length : 4) : step;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} data-testid="order-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0 rounded-2xl">
        <OrderStepper current={stepperIndex} />
        {done ? (
          <div data-testid="order-success" className="p-8 sm:p-12 text-center">
            <Mail size={48} className="mx-auto text-brand-teal" />
            <h3 className="font-display font-bold text-2xl mt-5">{t.verify.pending_title}</h3>
            <p data-testid="order-pending-sub" className="text-sm text-muted-foreground mt-3 leading-relaxed">{t.verify.pending_sub.replace("{email}", pendingEmail)}</p>
            <div className="mt-5 rounded-xl bg-brand-yellow/15 border border-brand-yellow/50 px-4 py-3 text-xs leading-relaxed text-left">
              {t.verify.pending_note}
            </div>
            {pendingOrderId && (
              <button type="button" data-testid="order-resend-verification" onClick={resendVerification} disabled={resending || resent}
                className="mt-4 inline-flex items-center justify-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark disabled:opacity-60 transition-colors">
                {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {resent ? t.verify.resent : t.verify.resend}
              </button>
            )}
            <div className="mt-6 flex justify-center">
              <button
                data-testid="order-close-button"
                onClick={() => onOpenChange(false)}
                className="px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors"
              >
                {t.order.close}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="order-form" noValidate>
            <Honeypot />
            <DialogHeader className="p-6 sm:p-8 pb-0 text-left">
              <DialogTitle className="font-display font-bold text-2xl tracking-tight">{t.order.title}</DialogTitle>
              <DialogDescription className="text-sm leading-relaxed">{mode === "new_krs_s24" && W.sub_s24 ? W.sub_s24 : mode === "new_krs_deed" && W.sub_deed ? W.sub_deed : t.order.sub}</DialogDescription>
            </DialogHeader>
            {restored && (
              <div data-testid="order-draft-restored" className="mx-6 sm:mx-8 mt-4 rounded-xl bg-brand-yellow/15 border border-brand-yellow/50 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 text-xs">
                <span className="flex-1 inline-flex items-center gap-2"><History size={13} className="shrink-0" /> {W.draft_restored}</span>
                <button type="button" data-testid="order-draft-reset-button" onClick={startOver} className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 hover:text-foreground">
                  <RotateCcw size={11} /> {W.draft_start_over}
                </button>
              </div>
            )}

            <div className="p-6 sm:p-8 space-y-6">
              {step === 0 && (
                <div data-testid="order-step-package-panel" className="rounded-xl bg-slate-100 dark:bg-slate-800 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t.order.package}>
                    <Select value={pkg.id} onValueChange={setPkgId}>
                      <SelectTrigger data-testid="order-package-select" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {packages.map((p) => {
                          const tp = (t.pricing.pkgs && t.pricing.pkgs[p.id]) || {};
                          return (
                            <SelectItem key={p.id} value={p.id} data-testid={`order-package-option-${p.id}`}>
                              {lang === "pl" ? p.name : tp.name || p.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t.order.billing}>
                    <Select value={effBilling} onValueChange={setBilling}>
                      <SelectTrigger data-testid="order-billing-select" className={inputCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableBillings.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`order-billing-option-${b}`}>
                            {billingLabels[b]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <div data-testid="order-summary" className="sm:col-span-2 flex items-baseline gap-2 text-sm">
                    <span className="font-display font-bold text-lg">{pkgName}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-mono font-semibold">{fmtPrice(price)} {t.order.per_month}</span>
                    {total && <span className="text-muted-foreground font-mono text-xs">({fmtPrice(total)} {t.pricing.net_unit} {t.order.upfront})</span>}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="order-step-mode-panel" className="space-y-4">
                  <ModeSelector choice={choice} onChange={(c) => { setChoice(c); setErrors({}); }} />
                  {!mode && <p data-testid="order-step-mode-hint" className="text-xs text-muted-foreground">{W.step_mode_hint}</p>}
                </div>
              )}

              {step === 2 && mode && (
                <div data-testid={`order-fields-${mode}`} className="space-y-6">
                  <OrderFields mode={mode} form={form} set={set} setForm={setForm} errors={errors} clearErr={clearErr} onRegistry={fillFromRegistry} companyLocked={mode === "existing" && !regUnavailable} onLookupStatus={onLookupStatus} signatories={signatories} setSignatories={setSignatories} emailExtra={
                    <div data-testid="order-extra-emails-section" className="space-y-2">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.verify.emails_label}</p>
                      {extraEmails.map((em, i) => (
                        <div key={i} className="space-y-1">
                          <div className="flex gap-2 items-start">
                            <Input data-testid={`order-extra-email-${i}`} data-field={`xemail_${i}`} aria-invalid={!!errors[`xemail_${i}`]} type="email" value={em} placeholder="e-mail" onChange={(e) => { const v = [...extraEmails]; v[i] = e.target.value; setExtraEmails(v); if (errors[`xemail_${i}`]) setErrors({ ...errors, [`xemail_${i}`]: undefined }); }} className={`bg-background ${errCls(errors[`xemail_${i}`])}`} />
                            <button type="button" data-testid={`order-extra-email-remove-${i}`} aria-label={t.verify.emails_remove} onClick={() => setExtraEmails(extraEmails.filter((_, j) => j !== i))} className="h-10 w-10 rounded-full inline-flex items-center justify-center text-brand-red hover:bg-brand-red/10 transition-colors shrink-0"><Trash2 size={15} /></button>
                          </div>
                          <FieldError id={`order-xemail_${i}`} msg={errors[`xemail_${i}`]} />
                        </div>
                      ))}
                      {extraEmails.length < 5 && (
                        <button type="button" data-testid="order-extra-email-add" onClick={() => setExtraEmails([...extraEmails, ""])} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-teal hover:text-brand-tealDark transition-colors"><Plus size={13} /> {t.verify.emails_add}</button>
                      )}
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{t.verify.emails_note}</p>
                    </div>
                  } />

                  {mode !== "new_krs_s24" && <ProxiesFields proxies={proxies} onChange={setProxies} />}

                  {mode === "new_krs_s24" && (
                    <div data-testid="order-s24-info" className="rounded-xl border border-border bg-slate-50 dark:bg-slate-900 p-4 text-xs text-muted-foreground leading-relaxed">
                      {W.s24_info.replace("{m}", String(BILLING_MONTHS[effBilling] || 1))}
                    </div>
                  )}

                  {mode !== "new_krs_s24" && extraFields.length > 0 && (
                    <div data-testid="order-extra-section" className="space-y-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.order.section_extra}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {extraFields.map((f) => {
                          const label = (lang === "pl" ? f.label : f.label_en || f.label) + (f.required ? " *" : "");
                          const ek = `x_${f.key}`;
                          const val = extra[f.key] || "";
                          const cls = `${inputCls} ${errCls(errors[ek])}`;
                          return (
                            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                              <Field label={label}>
                                {f.type === "textarea" ? (
                                  <Textarea data-testid={`order-extra-${f.key}`} data-field={ek} aria-invalid={!!errors[ek]} rows={3} value={val} onChange={(e) => setExtraVal(f.key, e.target.value)} className={`${cls} resize-none`} />
                                ) : f.type === "select" ? (
                                  <Select value={val} onValueChange={(v) => setExtraVal(f.key, v)}>
                                    <SelectTrigger data-testid={`order-extra-${f.key}`} data-field={ek} aria-invalid={!!errors[ek]} className={cls}>
                                      <SelectValue placeholder="—" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {f.options.map((o) => <SelectItem key={o} value={o} data-testid={`order-extra-${f.key}-option`}>{o}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input data-testid={`order-extra-${f.key}`} data-field={ek} aria-invalid={!!errors[ek]} type={f.type === "date" ? "date" : "text"} value={val} onChange={(e) => setExtraVal(f.key, e.target.value)} className={cls} />
                                )}
                                <FieldError id={`order-${ek}`} msg={errors[ek]} />
                              </Field>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div data-field="consent" tabIndex={-1}>
                    <label className="flex items-start gap-3 cursor-pointer text-xs text-muted-foreground leading-relaxed">
                      <Checkbox data-testid="order-consent-checkbox" checked={consent} onCheckedChange={(v) => { setConsent(v); clearErr("consent"); }} className={`mt-0.5 ${errors.consent ? "border-rose-500" : ""}`} />
                      <span>
                        {t.order.consent}{" "}
                        <a data-testid="order-rodo-link" href="/biuro-wirtualne-warszawa/rodo" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="underline underline-offset-2 font-semibold text-foreground hover:text-slate-500">
                          {t.order.consent_link}
                        </a>.
                        <OwuStatement testid="order-owu-link" />
                      </span>
                    </label>
                    <FieldError id="order-consent" msg={errors.consent} />
                  </div>
                </div>
              )}

              {step === 3 && mode && (
                <div className="space-y-6">
                  <OrderSummary pkgName={pkgName} billingLabel={billingLabels[effBilling]} price={price} total={total} mode={mode} form={isKrs ? { ...form, representative: signatoriesStr } : form} proxies={proxies} extra={extra} extraFields={mode === "new_krs_s24" ? [] : extraFields} goTo={goTo} consent={consent} />
                  <GuardNotice />
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-2">
                {step === 0 ? (
                  <button type="button" data-testid="order-cancel-button" onClick={() => onOpenChange(false)} className="px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors">
                    {t.order.cancel}
                  </button>
                ) : (
                  <button type="button" data-testid="order-back-button" onClick={() => goTo(step - 1)} className="px-6 py-3 rounded-full text-sm font-semibold border border-border hover:bg-muted transition-colors inline-flex items-center justify-center gap-2">
                    <ArrowLeft size={15} /> {W.back}
                  </button>
                )}
                {step < 3 ? (
                  <button
                    key="next"
                    type="button"
                    data-testid="order-next-button"
                    onClick={next}
                    disabled={step === 1 && !mode}
                    className="bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-50 disabled:cursor-not-allowed font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {W.next} <ArrowRight size={15} />
                  </button>
                ) : (
                  <button
                    key="submit"
                    type="submit"
                    data-testid="order-submit-button"
                    disabled={sending}
                    className="bg-brand-teal text-white hover:bg-brand-tealDark disabled:opacity-60 font-semibold px-8 py-3 rounded-full text-sm transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {sending && <Loader2 size={15} className="animate-spin" />}
                    {sending ? t.order.sending : mode === "new_krs_deed" ? W.submit_deed : mode === "new_krs_s24" ? W.submit_s24 : t.order.submit}
                  </button>
                )}
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
